import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    History as HistoryIcon,
    Loader2,
    RotateCcw,
    Trash2,
} from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
    listQuoteTemplateHistory,
    restoreQuoteTemplateVersion,
    deleteQuoteTemplateVersion,
} from "../lib/api";

/**
 * iter-180 — Quote template version history.
 * Lists snapshots taken before every update / restore / delete of a
 * template. Lets the contractor restore a prior look (logo + company
 * copy) after a rebrand goes sideways. Retention is 30 snapshots per
 * template; snapshotting is coalesced (5-minute window) server-side.
 */
function relativeAgo(iso) {
    if (!iso) return "";
    const now = Date.now();
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return iso;
    const s = Math.max(0, Math.floor((now - t) / 1000));
    if (s < 45) return "just now";
    if (s < 90) return "1 min ago";
    const m = Math.floor(s / 60);
    if (m < 45) return `${m} min ago`;
    if (m < 90) return "1 hour ago";
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hours ago`;
    const d = Math.floor(h / 24);
    if (d < 2) return "yesterday";
    if (d < 30) return `${d} days ago`;
    return new Date(iso).toLocaleDateString();
}

const LABEL_COPY = {
    auto: "Edit",
    "before-restore": "Before restore",
    "before-delete": "Before delete",
    manual: "Manual save",
};

export default function QuoteTemplateHistoryDialog({
    open,
    onOpenChange,
    templateId,
    templateName,
    onRestored,
}) {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState("");

    useEffect(() => {
        if (!open || !templateId) return;
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const rows = await listQuoteTemplateHistory(templateId);
                if (!cancelled) setVersions(rows);
            } catch (err) {
                if (!cancelled) {
                    toast.error(
                        err?.response?.data?.detail
                            || "Couldn't load history",
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, templateId]);

    const doRestore = async (v) => {
        setBusyId(v.id);
        try {
            const restored = await restoreQuoteTemplateVersion(templateId, v.id);
            toast.success("Template restored");
            onRestored && onRestored(restored);
            // Reload — the restore itself creates a new "before-restore" snapshot.
            const rows = await listQuoteTemplateHistory(templateId);
            setVersions(rows);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Restore failed");
        } finally {
            setBusyId("");
        }
    };

    const doDelete = async (v) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm("Delete this snapshot? It cannot be recovered.")) return;
        setBusyId(v.id);
        try {
            await deleteQuoteTemplateVersion(v.id);
            setVersions((prev) => prev.filter((x) => x.id !== v.id));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        } finally {
            setBusyId("");
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl bg-[#0A1128] border-[#2A3655] text-white"
                data-testid="quote-history-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <HistoryIcon className="w-4 h-4 text-[#00A3FF]" />
                        History — {templateName}
                    </DialogTitle>
                    <DialogDescription className="text-slate-400 text-sm">
                        Snapshots are taken before every change. Retention: 30
                        most-recent snapshots per template.
                    </DialogDescription>
                </DialogHeader>

                <div className="max-h-[60vh] overflow-y-auto space-y-2 -mx-1 px-1">
                    {loading && (
                        <div className="flex items-center gap-2 text-slate-400 text-sm py-6 justify-center">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading history…
                        </div>
                    )}
                    {!loading && versions.length === 0 && (
                        <div className="text-center py-8 text-slate-400 text-sm">
                            No snapshots yet. As you edit this template, older
                            versions will appear here.
                        </div>
                    )}
                    {versions.map((v) => {
                        const snap = v.snapshot || {};
                        const addr = (snap.company_address_lines || [])
                            .filter(Boolean)
                            .join(" · ");
                        return (
                            <div
                                key={v.id}
                                data-testid={`quote-history-row-${v.id}`}
                                className="flex items-start gap-3 p-3 rounded-sm border border-[#2A3655] bg-[#0F1B3A]/60 hover:border-[#00A3FF]/50 transition-colors"
                            >
                                <div className="shrink-0">
                                    {snap.logo_data_url ? (
                                        <img
                                            src={snap.logo_data_url}
                                            alt="logo"
                                            className="w-12 h-12 rounded object-cover bg-white/5"
                                        />
                                    ) : (
                                        <div className="w-12 h-12 rounded bg-[#0A1128] border border-[#2A3655] flex items-center justify-center text-slate-500 text-[10px]">
                                            no logo
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-bold text-white truncate">
                                            {snap.company_name || "— no company name —"}
                                        </span>
                                        <span
                                            className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-[#2A3655] text-slate-300"
                                            title={v.created_at}
                                        >
                                            {LABEL_COPY[v.label] || v.label}
                                        </span>
                                    </div>
                                    <div className="text-xs text-slate-400 mt-0.5">
                                        {relativeAgo(v.created_at)}
                                        {" · "}
                                        <span
                                            className="text-slate-500"
                                            title={v.created_at}
                                        >
                                            {new Date(v.created_at).toLocaleString()}
                                        </span>
                                    </div>
                                    {addr && (
                                        <div className="text-[11px] text-slate-500 mt-1 truncate">
                                            {addr}
                                        </div>
                                    )}
                                    {snap.company_phone && (
                                        <div className="text-[11px] text-slate-500 truncate">
                                            {snap.company_phone}
                                            {snap.company_email && ` · ${snap.company_email}`}
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col gap-1.5 shrink-0">
                                    <Button
                                        type="button"
                                        size="sm"
                                        onClick={() => doRestore(v)}
                                        disabled={busyId === v.id}
                                        data-testid={`quote-history-restore-${v.id}`}
                                        className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold h-8"
                                    >
                                        {busyId === v.id ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                            <>
                                                <RotateCcw className="w-3 h-3 mr-1" />
                                                Restore
                                            </>
                                        )}
                                    </Button>
                                    <button
                                        type="button"
                                        onClick={() => doDelete(v)}
                                        disabled={busyId === v.id}
                                        data-testid={`quote-history-delete-${v.id}`}
                                        title="Delete this snapshot"
                                        className="text-slate-500 hover:text-rose-400 text-xs flex items-center gap-1 justify-center py-1"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </DialogContent>
        </Dialog>
    );
}
